import { memo, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Fab, Grid, IconButton, Modal, Paper, SpeedDial, SpeedDialAction, Tooltip, Typography } from '@mui/material';
import { selectCurrentFolderConnectors, selectLastOpenedPendingAssetLoaction } from '@/redux/selectors/fileManagerSelectors';
import { Spinner } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import useAspera from '@/components/_hooks/useAspera';
import DragDropFiles from './FileManagerUpload/DragDropFiles';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import SigniantUpload from './SigniantUpload';
import { getFolder, resetFileManager } from '@/redux/slices/fileManagerSlice';

const FileManagerUpload = () => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSigniantModal, setShowSigniantModal] = useState(false);
  const lastLocation = useAppSelector(selectLastOpenedPendingAssetLoaction);
  const { upload } = useAspera();

  const { is_read_only: isReadOnly, ...connectors } = useAppSelector(selectCurrentFolderConnectors);

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const firstConnector = Object.keys(connectors).find((key) => connectors[key]?.allows_upload === true);
  const validConnectors = Object.keys(connectors).filter((key) => connectors[key]?.allows_upload === true);
  const noConnectors = validConnectors.length === 0;

  const ConnectorConfig = {
    aws_s3: {
      label: 'S3 Upload',
      onClick: openModal
    },
    aspera_node: {
      label: 'Aspera Upload',
      onClick: upload
    },
    signiant: {
      label: 'Signiant Upload',
      onClick: () => setShowSigniantModal(true)
    }
  };

  useEffect(() => {
    if (lastLocation) dispatch(getFolder({ path: lastLocation }));
    return () => {
      dispatch(resetFileManager());
    };
  }, []);

  return (
    <>
      <Box sx={{ position: 'absolute', right: 0, bottom: 0, zIndex: 1, mb: 3, minWidth: 85 }}>
        <div>
          {!isReadOnly && validConnectors.length > 1 ? (
            <UploadSpeedDial
              ariaLabel="Upload speed dial"
              icon={<PolarisIcon icon="cloud_upload" height={28} />}
              onClose={() => setSpeedDialOpen(false)}
              onOpen={() => setSpeedDialOpen(true)}
              open={speedDialOpen}
            >
              {validConnectors.map((connector) => {
                console.log(connector);
                if (connector) {
                  return (
                    <SpeedDialAction
                      key={connector}
                      icon={<span>{ConnectorConfig[connector]?.label}</span>}
                      onClick={ConnectorConfig[connector]?.onClick}
                    />
                  );
                }
              })}
            </UploadSpeedDial>
          ) : (
            <Tooltip title={noConnectors ? 'Uploads to folder not allowed' : ConnectorConfig[firstConnector]?.label} placement="top">
              <span>
                <Fab color="primary" aria-label="add" onClick={ConnectorConfig[firstConnector]?.onClick} disabled={noConnectors}>
                  <PolarisIcon icon="cloud_upload" height={28} />
                </Fab>
              </span>
            </Tooltip>
          )}
        </div>
      </Box>

      {connectors.signiant?.allows_upload && showSigniantModal && (
        <SigniantUpload open={showSigniantModal} onClose={() => setShowSigniantModal(false)} />
      )}

      <UploadModal open={open} aria-labelledby="upload-modal" aria-describedby="upload to folder" onClose={() => setOpen(false)}>
        <UploadModalContent>
          <div>
            <Grid container sx={{ p: 3 }}>
              {uploading && (
                <LoadingOverlayGrid>
                  <Spinner />
                </LoadingOverlayGrid>
              )}
              <Grid item container alignItems="center">
                <Box sx={{ color: 'primary.main', height: 40 }}>
                  <PolarisIcon icon="cloud_upload" height={40} />
                </Box>
                <Typography variant="h5" sx={{ ml: 1.5 }}>
                  S3 Upload Files
                </Typography>
                <IconButton onClick={() => setOpen(false)} sx={{ ml: 'auto' }} size="large">
                  <PolarisIcon icon="close" />
                </IconButton>
              </Grid>
              <DragDropFiles setUploading={setUploading} uploading={uploading} closeModal={closeModal} />
            </Grid>
          </div>
        </UploadModalContent>
      </UploadModal>
    </>
  );
};

export default memo(FileManagerUpload);

const LoadingOverlayGrid = styled(Grid)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.palette.background.default}c9;
  z-index: 5;
`;

const UploadModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadModalContent = styled(Paper)`
  width: 100%;
  max-width: 600px;
  display: flex;
  padding: ${({ theme }) => theme.spacing(0, 1)};
  position: relative;
`;

const UploadSpeedDial = styled(SpeedDial)`
  .MuiSpeedDial-actions .MuiFab-root {
    width: auto;
    text-transform: none;
    border-radius: 5px;
    height: auto;
    background-color: ${(props) => props.theme.palette.background.default};
    font-size: 1.1rem;
    padding: ${(props) => props.theme.spacing(1)};
    &:hover {
      color: ${(props) => props.theme.palette.primary.main};
    }
  }
`;
